import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Maximize2 } from "lucide-react";
import { fetchGalleries } from "../lib/cms";
import { imageUrl, srcSet } from "../lib/images";
import Lightbox from "../components/Lightbox";

// De officielle ydelser fra Sehested Tømrerfirma ApS
const SERVICE_CATEGORIES = [
  { id: "totalrenovering", title: "Totalrenovering" },
  { id: "ombygning", title: "Ombygning" },
  { id: "totalentreprise", title: "Totalentreprise" },
  { id: "tilbygninger", title: "Tilbygninger" },
  { id: "traeterrasser", title: "Træterrasser" },
  { id: "doere-og-vinduer", title: "Døre og vinduer" },
  { id: "tagarbejde", title: "Tagarbejde" },
];

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("alle");
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const data = await fetchGalleries();
      setGalleries(data);
      setLoading(false);
    }
    loadData();
  }, []);

  // Map each gallery into our service categories
  const allImages = useMemo(() => {
    const list = [];

    galleries.forEach((gallery) => {
      const gId = gallery.gallery_id;
      const images = gallery.images || [];

      // Map raw gallery IDs to official Ydelser IDs
      let targetServiceIds = [];
      if (gId === "renovering") {
        targetServiceIds = ["totalrenovering", "totalentreprise"];
      } else if (gId === "til-og-ombygning") {
        targetServiceIds = ["ombygning", "tilbygninger"];
      } else if (gId === "traeterrasse") {
        targetServiceIds = ["traeterrasser"];
      } else if (gId === "doere-og-vinduer" || gId === "horn-vinduesfabrik") {
        targetServiceIds = ["doere-og-vinduer"];
      } else if (gId === "tagarbejde") {
        targetServiceIds = ["tagarbejde"];
      } else {
        targetServiceIds = [gId];
      }

      images.forEach((imgUrl) => {
        targetServiceIds.forEach((sId) => {
          list.push({
            url: imgUrl,
            serviceId: sId,
          });
        });
      });
    });

    return list;
  }, [galleries]);

  // Only show categories that have images
  const categories = useMemo(() => {
    return SERVICE_CATEGORIES.filter((cat) =>
      allImages.some((img) => img.serviceId === cat.id)
    );
  }, [allImages]);

  // Filtered image list
  const filteredImages = useMemo(() => {
    if (activeFilter === "alle") {
      // Deduplicate when showing all images
      const seen = new Set();
      return allImages.filter((item) => {
        if (seen.has(item.url)) return false;
        seen.add(item.url);
        return true;
      });
    }
    const seen = new Set();
    return allImages.filter((item) => {
      if (item.serviceId !== activeFilter) return false;
      if (seen.has(item.url)) return false;
      seen.add(item.url);
      return true;
    });
  }, [allImages, activeFilter]);

  const activeImageUrls = useMemo(() => {
    return filteredImages.map((img) => img.url);
  }, [filteredImages]);

  const handlePrev = () => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : activeImageUrls.length - 1));
  };

  const handleNext = () => {
    setLightboxIndex((prev) => (prev < activeImageUrls.length - 1 ? prev + 1 : 0));
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="bg-primary-50/30 py-8 md:py-12">
      <Helmet>
        <title>Galleri - Sehested Tømrerfirma ApS</title>
        <meta name="description" content="Galleri - Se billeder af vores arbejde hos Sehested Tømrerfirma ApS." />
        <meta property="og:title" content="Galleri - Sehested Tømrerfirma ApS" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Category Filter Tabs */}
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 md:mb-10">
            <button
              onClick={() => setActiveFilter("alle")}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeFilter === "alle"
                  ? "bg-brand-600 text-white shadow-sm"
                  : "bg-white text-primary-700 border border-primary-200 hover:border-brand-500 hover:text-brand-600"
              }`}
            >
              Alle
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveFilter(cat.id)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                  activeFilter === cat.id
                    ? "bg-brand-600 text-white shadow-sm"
                    : "bg-white text-primary-700 border border-primary-200 hover:border-brand-500 hover:text-brand-600"
                }`}
              >
                {cat.title}
              </button>
            ))}
          </div>
        )}

        {/* Pure Image Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {filteredImages.map((image, index) => (
            <div
              key={`${image.url}-${index}`}
              onClick={() => setLightboxIndex(index)}
              className="group relative aspect-[4/3] overflow-hidden rounded-xl bg-primary-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer border border-primary-100"
            >
              <img
                src={imageUrl(image.url, "width=800,quality=80,format=auto")}
                srcSet={srcSet(image.url, [400, 800, 1200])}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                alt="Galleri"
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-primary-950/0 transition-colors duration-300 group-hover:bg-primary-950/30 flex items-center justify-center">
                <div className="rounded-full bg-white/90 p-3 text-primary-900 opacity-0 shadow-md backdrop-blur-xs transition-all duration-300 group-hover:opacity-100 group-hover:scale-100 scale-75">
                  <Maximize2 className="h-5 w-5 text-brand-600" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox for full size viewing */}
      <Lightbox
        images={activeImageUrls}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </div>
  );
}
